import React, { useMemo } from 'react';
import { TabContent } from '@components/main-components/tabs/Tab';
import { ParamsStore } from '@context/params';
import type { CreditRoleType, SongViewFragment } from '@graphql/reactQuery';
import { TAB_CHOICES, type TabChoice } from '../Activity/store';
import { Audience } from '../Audience';
import { BehindTheMusicSectionV2 } from '../BehindTheMusic/BehindTheMusicV2';
import { CommentsPreview } from '../Comments/CommentsPreview';
import { ContractDetailsSection } from '../Contract';
import { RewardComponent } from '../Reward';
import { ShareAndEarnButton } from '../ShareAndEarnButton';
import { CreditSplitsSection } from '../Splits';

export const OverviewTab = ({
  releaseFrag,
  setActiveTab,
  tabsTopOffset,
}: {
  releaseFrag: SongViewFragment;
  setActiveTab?: (tab: TabChoice) => void;
  tabsTopOffset?: number;
}) => {
  const splits = useMemo(
    () =>
      releaseFrag.creditSplit?.creditAllocations.map(allocation => ({
        ownerAddress: allocation.owner.publicAddress,
        avatarUrl: allocation.owner?.avatar?.url,
        ...allocation,
        roles: allocation.roles as Array<CreditRoleType>,
      })),
    [releaseFrag.creditSplit?.creditAllocations],
  );

  const onSeeAll = () => {
    setActiveTab?.(TAB_CHOICES.COMMENTS);
    ParamsStore.song_tab = TAB_CHOICES.COMMENTS;
    window.scrollTo({ top: tabsTopOffset, behavior: 'instant' });
  };

  return (
    <TabContent value={TAB_CHOICES.OVERVIEW} className="mx-auto my-0 w-full max-w-content pt-8">
      <div className="px-4 md:px-6">
        <BehindTheMusicSectionV2 releaseFrag={releaseFrag} />
      </div>
      <div className="mt-12 px-4 md:px-6">
        <CommentsPreview releaseFrag={releaseFrag} onSeeAll={onSeeAll} />
      </div>
      <div className="px-4 md:px-6">
        <Audience releaseFrag={releaseFrag} isFullPage={false} tabsTopOffset={tabsTopOffset} />
      </div>
      <div className="px-4 md:px-6">
        <ShareAndEarnButton isBanner releaseFrag={releaseFrag} />
      </div>
      <RewardComponent releaseFrag={releaseFrag} />
      <div className="box-border flex flex-col">
        {!!splits && (
          <div className="px-4 md:px-6">
            <CreditSplitsSection splits={splits} />
          </div>
        )}
        <div className="px-4 md:px-6">
          <ContractDetailsSection
            mintStartTime={releaseFrag.mintStartTime}
            contractAddress={releaseFrag.contractAddress}
            splitsContractAddress={releaseFrag.creditSplit?.splitAddress}
            chainId={releaseFrag.chainId}
            mints={releaseFrag.totalMinted}
            totalVolume={releaseFrag.saleDetails?.totalVolume}
            uniqueCollectors={releaseFrag.collectorsCount}
          />
        </div>
      </div>
    </TabContent>
  );
};
